import {
  diaNegocio,
  formatearMoneda,
  formatearTelefono,
  nombreServicios,
  serviciosDe,
  type CitaVista,
  type ClienteVista,
  type ConfigItem,
} from "@barbones/shared";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useNavigate } from "@tanstack/react-router";
import {
  CalendarClock,
  ContactRound,
  Loader2,
  Package,
  Settings2,
  UserCog,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/cliente";
import { useSesion } from "@/api/sesion";
import { useTiempoReal } from "@/api/tiempo-real";
import { fechaHora } from "@/lib/utils";
import { CommandPalette, type Comando } from "./CommandPalette";
import { TopBar } from "./TopBar";
import {
  BannerConexion,
  SincronizacionProvider,
} from "@/api/offline/sincronizacion";
import { destinosDelBuscador } from "./comandos";

interface Sucursal {
  id: string;
  nombre: string;
}

interface ResultadosBusqueda {
  clientes: ClienteVista[];
  citas: CitaVista[];
  productos: {
    id: string;
    sku: string;
    nombre: string;
    precioVenta: number;
    stockVenta: number;
    stockReservado: number;
  }[];
  usuarios: { id: string; nombre: string; usuario: string; rol: string }[];
}

/** Retrasa un valor para no consultar al servidor en cada tecla. */
function useDiferido<T>(valor: T, ms = 250): T {
  const [diferido, setDiferido] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setDiferido(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return diferido;
}

/** Estructura de la aplicación autenticada: barra superior, buscador global y contenido. */
export function AppShell() {
  const { estado, usuario, puede } = useSesion();
  const navegar = useNavigate();
  const [buscadorAbierto, setBuscadorAbierto] = useState(false);
  const [consulta, setConsulta] = useState("");
  const consultaDiferida = useDiferido(consulta.trim());
  useTiempoReal(estado.tipo === "autenticado");

  const remotos = useQuery({
    queryKey: ["buscar", consultaDiferida],
    queryFn: () =>
      api<ResultadosBusqueda>("/buscar", { query: { q: consultaDiferida } }),
    enabled: estado.tipo === "autenticado" && consultaDiferida.length >= 2,
    staleTime: 10_000,
  });

  const sucursales = useQuery({
    queryKey: ["sucursales"],
    queryFn: () => api<Sucursal[]>("/sucursales"),
    enabled: estado.tipo === "autenticado",
    staleTime: 5 * 60_000,
  });

  const configuraciones = useQuery({
    queryKey: ["configuracion"],
    queryFn: () => api<ConfigItem[]>("/configuracion"),
    enabled: estado.tipo === "autenticado" && puede("config.ver"),
    staleTime: 60_000,
  });

  useEffect(() => {
    const alPresionar = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setBuscadorAbierto((v) => !v);
      }
    };
    window.addEventListener("keydown", alPresionar);
    return () => window.removeEventListener("keydown", alPresionar);
  }, []);

  const comandos = useMemo<Comando[]>(() => {
    const permisos = usuario?.permisos ?? [];
    const pantallas: Comando[] = destinosDelBuscador(permisos).map((d) => ({
      id: d.id,
      grupo: d.search ? "Reportes" : "Pantallas",
      etiqueta: d.etiqueta,
      palabrasClave: d.palabrasClave,
      icono: <d.icono />,
      accion: () => void navegar({ to: d.to, search: d.search }),
    }));
    const configs: Comando[] = (configuraciones.data ?? []).map((c) => ({
      id: `cfg-${c.clave}`,
      grupo: "Configuraciones",
      etiqueta: c.titulo,
      descripcion: `${c.categoria} · ${c.descripcion}`,
      palabrasClave: c.clave,
      icono: <Settings2 />,
      accion: () =>
        void navegar({ to: "/configuracion", search: { clave: c.clave } }),
    }));
    return [...pantallas, ...configs];
  }, [usuario, configuraciones.data, navegar]);

  const resultadosRemotos = useMemo<Comando[]>(() => {
    const r = remotos.data;
    if (!r) return [];
    return [
      ...r.clientes.map((c) => ({
        id: `cli-${c.id}`,
        grupo: "Clientes",
        etiqueta: c.nombre,
        descripcion: formatearTelefono(c.telefono),
        icono: <ContactRound />,
        accion: () => void navegar({ to: "/clientes", search: { id: c.id } }),
      })),
      ...r.citas.map((c) => ({
        id: `cita-${c.id}`,
        grupo: "Citas próximas",
        etiqueta: `${c.cliente.nombre} · ${nombreServicios(serviciosDe(c))}`,
        descripcion: `${fechaHora(c.inicio)}${c.barbero ? ` con ${c.barbero}` : " · sin barbero"}`,
        icono: <CalendarClock />,
        accion: () =>
          void navegar({
            to: "/agenda",
            search: { dia: diaNegocio(new Date(c.inicio)), cita: c.id },
          }),
      })),
      ...r.productos.map((p) => ({
        id: `prod-${p.id}`,
        grupo: "Productos",
        etiqueta: `${p.nombre} (${p.sku})`,
        descripcion: `${formatearMoneda(p.precioVenta)} · ${p.stockVenta - p.stockReservado} disponibles`,
        icono: <Package />,
        accion: () => void navegar({ to: "/inventario", search: { id: p.id } }),
      })),
      ...r.usuarios.map((u) => ({
        id: `usr-${u.id}`,
        grupo: "Usuarios",
        etiqueta: u.nombre,
        descripcion: `@${u.usuario}`,
        icono: <UserCog />,
        accion: () => void navegar({ to: "/configuracion/usuarios" }),
      })),
    ];
  }, [remotos.data, navegar]);

  if (estado.tipo === "cargando") {
    return (
      <div
        className="grid min-h-dvh place-items-center"
        role="status"
        aria-live="polite"
      >
        <Loader2
          className="size-6 animate-spin text-accent"
          aria-label="Cargando"
        />
      </div>
    );
  }
  if (estado.tipo === "anonimo") return <Navigate to="/login" />;

  return (
    <SincronizacionProvider>
      <div className="flex min-h-dvh flex-col">
        <a
          href="#contenido"
          className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:p-2"
        >
          Saltar al contenido
        </a>
        <BannerConexion />
        <TopBar
          onAbrirBuscador={() => setBuscadorAbierto(true)}
          sucursales={sucursales.data ?? []}
        />
        <main id="contenido" className="flex-1">
          <Outlet />
        </main>
        <CommandPalette
          abierto={buscadorAbierto}
          onCambiarAbierto={setBuscadorAbierto}
          comandos={comandos}
          resultadosRemotos={
            consulta.trim().length >= 2 ? resultadosRemotos : []
          }
          onConsulta={setConsulta}
          cargando={remotos.isFetching}
        />
      </div>
    </SincronizacionProvider>
  );
}
